/**
 * Tutorial Video Modal Component
 *
 * Displays a tutorial video in a lightbox-style modal dialog.
 * Renders the full embed code HTML inside a responsive 16:9 container.
 * Follows the HelpContentSheet pattern from the project area settings.
 *
 * @see {@link ../lib/tutorial-videos.ts} for embed code configuration
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@bnsn/ui/components/dialog'
import { Button } from '@bnsn/ui/components/button'

interface TutorialVideoModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback when the open state changes */
  onOpenChange: (open: boolean) => void
  /** Full HTML embed code (e.g. an iframe tag) */
  embedCode: string
}

/**
 * Tutorial Video Modal
 *
 * Renders a dialog with an embedded video using the full embed code HTML.
 * The embed container forces child iframes to fill the 16:9 space responsively.
 *
 * SECURITY NOTE: The embedCode prop is sourced exclusively from developer-controlled
 * constants in tutorial-videos.ts (not from user input or external sources),
 * so rendering it directly is safe from XSS.
 */
export const TutorialVideoModal = ({ isOpen, onOpenChange, embedCode }: TutorialVideoModalProps): JSX.Element => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-overlay" />
      </DialogPortal>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col">
        <DialogTitle className="sr-only">Tutorial Video</DialogTitle>
        <DialogDescription className="sr-only">Tutorial video player</DialogDescription>

        {/* Video embed container - 16:9 aspect ratio. Only mounted when open so video stops on close. */}
        <div className="overflow-y-auto flex-grow">
          {/* embedCode is from developer-controlled constants, not user input */}
          {isOpen && (
            <div
              className="relative w-full h-0 pb-[56.25%] [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:w-full [&_iframe]:h-full"
              dangerouslySetInnerHTML={{ __html: embedCode }}
            />
          )}
        </div>

        <DialogFooter className="pt-4 mt-2">
          <Button className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
